
import React, { useEffect, useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { base_url } from 'Backend/api';
import { useNavigate } from 'react-router-dom';
import { getStoreRatingParts } from 'utils/storeRatingDisplay';
import { formatStoreTime12h } from 'utils/formatStoreTime';

function resolvePromoVideoSrc(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const path = trimmed.replace(/^\/+/, '');
  return `${base_url}/storage/app/public/${path}`;
}

const RestaurantHeader = ({ restaurant, isFavorite = false, favoriteLoading = false, onFavoriteToggle }) => {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const { averageFormatted, reviewsCount } = getStoreRatingParts(restaurant);

  const rawPromoVideo = restaurant?.media?.promo_video_url;
  const promoVideoPath =
    typeof rawPromoVideo === 'string' ? rawPromoVideo.trim() : '';
  const hasPromoVideo = promoVideoPath.length > 0;
  const promoVideoSrc = hasPromoVideo ? resolvePromoVideoSrc(promoVideoPath) : '';

  useEffect(() => {
    if (!showVideo) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowVideo(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showVideo]);

  return (
    <div className="w-full font-[cairo] mt-6">

      {/* IMAGE HEADER */}
      <div className="relative h-[120px]">

        <Image
          src={`${base_url}/storage/app/public/${restaurant?.media?.main_image}`}
          alt={restaurant?.name}
          className="w-full h-[120px] object-cover"
        />

        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 bg-white p-2 rounded-full shadow z-20"
        >
          <Icon name="ArrowRight" size={18} />
        </button>

        <button
          type="button"
          onClick={onFavoriteToggle}
          disabled={favoriteLoading}
          className="absolute top-4 left-4 rounded-full bg-white p-2 shadow disabled:opacity-60"
          aria-label={isFavorite ? "إزالة من المفضلة" : "إضافة إلى المفضلة"}
        >
          <Icon
            name="Heart"
            size={18}
            color={isFavorite ? "#F93209" : "#9CA3AF"}
            fill={isFavorite ? "#F93209" : "none"}
          />
        </button>
      </div>

      {/* INFO */}
      <div className="bg-white px-4 py-3 flex items-center justify-between">

        <div className='flex flex-row'>
          {/* Rating */}
          <div className="bg-[#00C818] text-white px-1 py-0 rounded-[4px] text-sm font-bold text-center ml-4">
            <div className='flex flex-row items-center gap-1'>
              <img src='/assets/images/starrate.svg' className='h-4 w-4' alt="" />
              {averageFormatted}
            </div>
            <div className="text-[10px] font-medium">
              + {reviewsCount}
            </div>
          </div>

          {/* NAME + INFO */}
          <div className="text-right">
            <div className="text-[15px] font-bold">
              {restaurant?.name}
            </div>

            <div className='flex flex-row gap-4 items-center'>
              <div className="text-[10px] text-gray-400 flex items-center gap-1">
                <Icon name="Clock" size={12} />
                <span>
                  {formatStoreTime12h(restaurant?.timing?.opens_at)} - {formatStoreTime12h(restaurant?.timing?.closes_at)}
                </span>
              </div>

              <div className="text-xs text-gray-500">
                {restaurant?.type}
              </div>
            </div>
          </div>
        </div>

        {/* VIDEO BUTTON */}
        <div>
          {hasPromoVideo ? (
            <button
              type="button"
              onClick={() => setShowVideo(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F93209] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#e02d08] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F93209] focus-visible:ring-offset-2"
            >
              
              فيديو
            </button>
          ) : (
            <span
              className="inline-flex cursor-not-allowed select-none items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-400"
              aria-disabled="true"
              title="لا يوجد فيديو متاح"
            >
              
              فيديو
            </span>
          )}
        </div>
      </div>

      {/* 🎬 VIDEO MODAL */}
      {showVideo && hasPromoVideo && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-2 backdrop-blur-sm sm:p-4"
          role="presentation"
          onClick={() => setShowVideo(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="restaurant-promo-video-title"
            dir="rtl"
            className="relative w-full max-w-6xl overflow-hidden rounded-[16px] bg-[#111] shadow-2xl ring-1 ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
              <h2
                id="restaurant-promo-video-title"
                className="min-w-0 flex-1 truncate text-right font-[cairo] text-sm font-semibold text-white sm:text-base"
              >
                فيديو تعريفي — {restaurant?.name}
              </h2>
              <button
                type="button"
                onClick={() => setShowVideo(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                aria-label="إغلاق"
              >
                <Icon name="X" size={22} color="currentColor" />
              </button>
            </div>

            <div className="p-2 sm:p-4">
              <div className="relative flex min-h-[52svh] w-full items-center justify-center overflow-hidden rounded-lg bg-black ring-1 ring-white/5 sm:min-h-[58svh] md:min-h-[64svh]">
                <video
                  key={promoVideoSrc}
                  controls
                  playsInline
                  preload="metadata"
                  className="max-h-[78svh] w-full object-contain sm:max-h-[82svh]"
                  src={promoVideoSrc}
                >
                  المتصفح لا يدعم تشغيل هذا الفيديو.
                </video>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RestaurantHeader;